import { useEffect } from "react";

import { toGqlId } from "@src/api/formatters/shopify";
import { useFetchProductLazyQuery } from "@src/generated";
import NoImage from "@src/images/no_image.png";
import { useSelector } from "@src/store";
import { formatPrice } from "@src/store/slices/agProducts/util/formatPrice";
import { parsePrice } from "@src/store/slices/agProducts/util/parsePrice";
import { getOrgCurrencyFormat } from "@src/store/slices/experiences/selectors/currency/getOrgCurrencyFormat";

const GQL_ID_TYPE = /^gid:\/\/shopify\/([^/]+)\//;

/**
 * `toGqlId` returns any string containing "gid" untouched, so a ProductVariant
 * gid would otherwise reach `product(id:)` — which Shopify rejects with a
 * top-level GraphQL error, failing the whole request. Anything that isn't a
 * bare id or a Product gid is not queryable here.
 */
const toProductGqlId = (productId?: string): string => {
  if (!productId) {
    return "";
  }
  const type = GQL_ID_TYPE.exec(productId)?.[1];
  if (type && type !== "Product") {
    return "";
  }
  return toGqlId(productId, "Product");
};

/**
 * Fetches the product only when the caller is actually missing the field it
 * asked for; `skip` is read on mount, matching the one-shot debounce below.
 */
const useProduct = (productId: string | undefined, skip: boolean) => {
  const shopifyId = toProductGqlId(productId);
  const [fetchProduct, { data: response }] = useFetchProductLazyQuery();

  useEffect(() => {
    if (skip || !shopifyId) {
      return;
    }
    const timeout = setTimeout(() => {
      void fetchProduct({ variables: { id: shopifyId } });
    }, 500);
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  return response?.product;
};

export const useProductImage = (productId?: string, image?: string): string => {
  const product = useProduct(productId, Boolean(image));
  return ((image || product?.featuredImage?.url) ?? NoImage) as string;
};

export const useProductTitle = (
  productId?: string,
  title?: string
): string | undefined => {
  const product = useProduct(productId, Boolean(title));
  return title || product?.title;
};

export const useProductVariantTitlePrice = (
  productId?: string,
  variantId?: string,
  price?: string
) => {
  const currencyFormat = useSelector(getOrgCurrencyFormat);

  const formattedVariantId = variantId
    ? toGqlId(variantId, "ProductVariant")
    : "";
  const product = useProduct(productId, Boolean(price));

  if (price) {
    return price;
  }
  const variant = product?.variants.edges.find(
    (e) => e.node.id === formattedVariantId
  );
  const value = variant?.node?.price;
  const parsed = parsePrice(value);
  return formatPrice(parsed, currencyFormat);
};
